<template>
    <div class="automation-block-editor wait-for">
        <div class="editor-row customize-editor-minimize">
            <div class="editor-row-title">Wait For What?</div>
            <CustomPicker
                v-model="clone.mode"
                :items="waitForTypes"
                idKey="id"
                labelKey="label"
                title="Select Wait For What"
            />
        </div>

        <div class="editor-row customize-editor-minimize">
            <div class="editor-row-title">How long to wait?</div>
            <div class="btnGroup">
                <SupsisButton
                    v-for="mode in ['static', 'dynamic']"
                    :classNames="clone.endDate.mode === mode ? 'btnGroupItem selected' : 'btnGroupItem '"
                    :primary="clone.endDate.mode === mode"
                    :ghost="clone.endDate.mode !== mode"
                    @click="() => changeMode(mode)"
                    small
                    :key="mode"
                >
                    {{ mode }}
                </SupsisButton>
            </div>

            <div class="d-flex gap-5" v-if="clone.endDate.mode === 'static'" :key="clone.endDate.mode">
                <div class="editor-row customize-editor-minimize">
                    <div class="editor-row-title">Duration</div>
                    <TiptapEditor
                        placeholder="Duration"
                        :show-attributes="true"
                        v-model="clone.endDate.static.value"
                        automation-mode
                        key="static"
                    />
                </div>
                <div class="editor-row customize-editor-minimize">
                    <div class="editor-row-title">Unit:</div>
                    <CustomPicker
                        v-model="clone.endDate.static.unit"
                        :items="units"
                        idKey="id"
                        labelKey="label"
                        title="Select Duration"
                    />
                </div>
            </div>
            <div class="d-flex gap-5" v-else>
                <div class="editor-row customize-editor-minimize">
                    <div class="editor-row-title">Pick a Future Date (isodate , timestamp)</div>
                    <TiptapEditor
                        placeholder="Timestamp"
                        :show-attributes="true"
                        v-model="clone.endDate.dynamic.value"
                        automation-mode
                        key="dynamic"
                    />
                </div>
            </div>
        </div>
        <div class="editor-row customize-editor-minimize">
            <div class="editor-row-title">Match All?</div>
            <a-switch v-model="clone.match.all" />
        </div>
        <div class="editor-row customize-editor-minimize" v-if="!clone.match.all">
            <div class="editor-row-title">Match Media Messages?</div>
            <a-switch v-model="clone.match.matchMediaMessages" />
        </div>
        <div class="editor-row customize-editor-minimize" v-if="!clone.match.all">
            <div class="editor-row-title">Matching Messages</div>
            <SupsisButton @click="() => clone.match.messages.push('')" small ghost>+ Add Message</SupsisButton>
            <div class="d-flex flex-column gap-5">
                <TiptapEditor
                    placeholder="Matching Messages"
                    :show-attributes="true"
                    v-model="clone.match.messages[i]"
                    automation-mode
                    v-for="(message, i) in clone.match.messages"
                    :key="i"
                />
            </div>
        </div>
        <div class="editor-row customize-editor-minimize" v-if="!clone.match.all">
            <div class="editor-row-title">Matching Score Threshold</div>
            <a-input-number v-model="clone.match.minScore" :min="50" :max="100" />
        </div>

        <div
            class="editor-row customize-editor-minimize"
            v-if="['web-message', 'instagram-message', 'messenger-message', 'telegram-message'].includes(clone.mode)"
        >
            <div class="editor-row-title">Contact ID</div>
            <TiptapEditor placeholder="Contact ID" :show-attributes="true" v-model="clone.contactId" automation-mode />
        </div>
        <div class="editor-row customize-editor-minimize" v-else-if="clone.mode === 'email-message'">
            <div class="editor-row-title">Email From ?</div>
            <TiptapEditor placeholder="Email From" :show-attributes="true" v-model="clone.email.from" automation-mode />
        </div>
        <div class="editor-row customize-editor-minimize" v-else-if="clone.mode === 'whatsapp-message'">
            <div class="editor-row-title">Whatsapp Number</div>
            <TiptapEditor
                placeholder="Whatsapp Number"
                :show-attributes="true"
                v-model="clone.whatsappNumber"
                automation-mode
            />
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import CustomPicker from "../../../../../../components/pickers/CustomPicker.vue";

const log = log2("WaitFor");
export default {
    name: "WaitFor",
    components: { CustomPicker, SupsisButton, TiptapEditor },
    props: ["value"],
    data() {
        const clone = _.cloneDeep(this.value) || {
            mode: "duration", //duration, timestamp
            duration: {
                unit: "seconds",
                value: "1",
            },
            timestamp: {
                value: "",
            },
        };
        return {
            clone,
            units: [
                {
                    id: "seconds",
                    label: "Seconds",
                },
                {
                    id: "minutes",
                    label: "Minutes",
                },
                {
                    id: "hours",
                    label: "Hours",
                },
                {
                    id: "days",
                    label: "Days",
                },
            ],
            waitForTypes: [
                {
                    id: "whatsapp-message",
                    label: "Whatsapp Contact Message",
                    description: "Wait for a message(s) from a whatsapp number (via whatsapp channels)",
                },
                {
                    id: "web-message",
                    label: "Web Contact Message",
                    description: "Wait for a message(s) from a web contact (via webchat widget)",
                },
                {
                    id: "instagram-message",
                    label: "Instagram Contact Message",
                    description: "Wait for a message(s) from a instagram contact (via instagram channels)",
                },
                {
                    id: "messenger-message",
                    label: "Messenger Contact Message",
                    description: "Wait for a message(s) from a messenger contact (via messenger channels)",
                },
                {
                    id: "telegram-message",
                    label: "Telegram Contact Message",
                    description: "Wait for a message(s) from a telegram contact (via telegram channels)",
                },
                {
                    id: "email-message",
                    label: "Email Contact Message",
                    description: "Wait for a message(s) from a email contact (via email inbox)",
                },
            ],
        };
    },
    methods: {
        changeMode(mode) {
            this.clone.endDate.mode = mode;
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.automation-block-editor.wait-for {
    padding: 20px;
    display: flex;
    gap: 20px;
    flex-direction: column;

    section {
        width: 100%;
        display: flex;
        gap: 10px;
        flex-direction: column;
    }

    .editor-row {
        width: 100%;

        .editor-row-title {
            font-weight: bold;
            font-size: 16px;
        }
    }

    .btnGroup {
        display: flex;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
        background: var(--color-bg-neutral-default);
        margin: 2px 10px;
        padding: 2px;

        .btnGrupItem {
            &.selected {
                background: var(--color-bg-oncomponent-default);
                box-shadow: var(--shadow-interaction-200);
            }
        }

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }
    }
}
</style>
